<template>
    <div
        v-show="validMpCombinations.length || !isFirstTimeAllLoaded"
        v-gtm:type="'PopularParlay'"
        v-gtm:@view
        class="mpRecommendation"
    >
        <div
            v-gtm:@click
            class="header"
            v-on:click="toggleCollapse"
        >
            <div class="front">
                <VButton class="arrow">
                    <SvgIcon
                        name="icon-system-arrow"
                        size="sm"
                        v-bind:rotated="isCollapsed"
                    />
                </VButton>
                <div class="name">
                    {{ i18n.t('mp_combination_popular_mix_parlay') }}
                </div>
            </div>
        </div>
        <div
            v-show="!isCollapsed"
            v-bind:key="sportType"
            class="content"
        >
            <div class="mpCombinations">
                <div v-if="hasError" class="mpCombinations-empty">
                    <span>{{ i18n.t('general_something_went_wrong_message') }}</span>
                </div>
                <div v-else-if="!isFirstTimeAllLoaded" class="mpCombinations-loading">
                    <StaticSpinner />
                </div>
                <Swiper
                    v-show="isFirstTimeAllLoaded && !hasError"
                    v-bind:modules="[Navigation]"
                    v-bind:slidesPerView="2.2"
                    v-bind:spaceBetween="8"
                    v-bind:slidesPerGroup="1"
                    v-bind:navigation="true"
                    v-bind:breakpoints="{
                        1360: {
                            slidesPerView: 3.2,
                        },
                    }"
                    class="mpCombinations-content"
                >
                    <SwiperSlide v-for="(mpCombination, index) in validMpCombinations" v-bind:key="mpCombination.key">
                        <MpCombination
                            v-gtm:type="'Comb'"
                            v-bind:mpCombination="mpCombination"
                            v-bind:order="index + 1"
                        />
                    </SwiperSlide>
                </Swiper>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { Navigation } from 'swiper/modules';
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { ref, watch } from 'vue';
    import StaticSpinner from '@/components/common/StaticSpinner.vue';
    import SvgIcon from '@/components/common/SvgIcon.vue';
    import VButton from '@/components/common/button/VButton.vue';
    import MpCombination from '@/components/mpRecommendation/MpCombination.vue';
    import { MpCombinationsContext } from '@/components/mpRecommendation/MpCombinationsContext';
    import { useEventEmitter } from '@/composable/useEventEmitter';
    import { i18n } from '@/core/lib/i18n';
    import { RecommendationApi } from '@/core/lib/recommendationApi';
    import type { IMpCombination } from '@/interface/IPopularMixParlay';
    import { RecommendType } from '@/interface/enum';
    import { useAsiaSportMarketInfoStore } from '@/store/asiaSportMarketInfoStore';

    const isCollapsed = ref(false);

    function toggleCollapse() {
        isCollapsed.value = !isCollapsed.value;
    }

    const mpCombinations = ref<IMpCombination[]>([]);

    const { validMpCombinations, isAllLoaded } = MpCombinationsContext.provide(mpCombinations);

    const hasError = ref(false);

    const { sportType } = storeToRefs(useAsiaSportMarketInfoStore());
    const COMBINATION_COUNT = 5;
    async function fetchMpRecommendations() {
        try {
            hasError.value = false;
            const response = await RecommendationApi.getMpRecommendations(sportType.value, RecommendType.BetCount, COMBINATION_COUNT);
            mpCombinations.value = response;
        } catch (error) {
            console.error('Failed to fetch mp recommendations', error);
            hasError.value = true;
        }
    }

    fetchMpRecommendations();

    const isFirstTimeAllLoaded = ref(false);
    watch(isAllLoaded, () => {
        if (isAllLoaded.value) {
            isFirstTimeAllLoaded.value = true;
        }
    });

    const eventEmitter = useEventEmitter();
    eventEmitter.on('oddsDisplay:refreshMpRecommendation', () => {
        fetchMpRecommendations();
    });

    watch(sportType, () => {
        isFirstTimeAllLoaded.value = false;
        mpCombinations.value = [];
        fetchMpRecommendations();
    });
</script>

<style lang="scss" scoped>
    @import "@/components/mpRecommendation/MpRecommendation";
</style>

<style lang="scss">
    @import "swiper/css";
    @import "swiper/css/navigation";

    .mpCombinations-content {
        .swiper-wrapper {
            display: flex;
            align-items: stretch;
        }

        .swiper-slide {
            display: flex;
            height: auto;
        }

        .swiper-button-prev,
        .swiper-button-next {
            top: 0;
            height: 100%;
            margin-top: 0;
            background-color: var(--indigo-200);
            background-position: center;
            background-repeat: no-repeat;
            background-size: 20px auto;

            &.swiper-button-disabled {
                opacity: 0.15;
            }

            &::before {
                position: absolute;
                top: 4px;
                right: 4px;
                width: calc(100% - 8px);
                height: calc(100% - 8px);
                background: var(--brandColor-800);
                border-radius: 4px;
                content: "";
                opacity: 0;
                transition-duration: 0.5s;
            }

            &:hover::before {
                opacity: 0.08;
                transition-duration: 0.5s;
            }
        }

        .swiper-button-prev {
            left: 0;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' class='svgIcon' viewBox='0 0 96 96'%3E%3Cpath fill='%233A498E' fill-rule='evenodd' d='M65.535 20.535a5 5 0 1 0-7.07-7.07L27.466 44.462q0 .002-.002.002l-.09.093a5.01 5.01 0 0 0-1.008 5.325c.244.601.61 1.165 1.098 1.653l31 31a5 5 0 1 0 7.072-7.07L38.07 48z' clip-rule='evenodd'%3E%3C/path%3E%3C/svg%3E");

            &::after {
                position: absolute;
                right: -8px;
                width: 8px;
                height: 100%;
                background: linear-gradient(to left, transparent 0%, var(--indigo-200) 100%);
                content: "";
            }
        }

        .swiper-button-next {
            right: 0;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' class='svgIcon' viewBox='0 0 96 96'%3E%3Cpath fill='%233A498E' fill-rule='evenodd' d='M30.465 20.535a5 5 0 1 1 7.07-7.07l31 31a5 5 0 0 1 0 7.071l-31 31a5 5 0 1 1-7.07-7.072L57.928 48 30.464 20.535Z' clip-rule='evenodd'%3E%3C/path%3E%3C/svg%3E");

            &::after {
                position: absolute;
                left: -8px;
                width: 8px;
                height: 100%;
                background: linear-gradient(to right, transparent 0%, var(--indigo-200) 100%);
                content: "";
            }
        }
    }
</style>
